import { forwardRef, TextareaHTMLAttributes, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';

import { Field, controlClasses, useField } from '../field';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Grows with its text instead of scrolling. */
  autoGrow?: boolean;
  /** With `autoGrow`: the height in px where it stops growing and starts to scroll. */
  maxHeight?: number;
}

const Control = forwardRef<HTMLTextAreaElement, Omit<TextareaProps, 'label' | 'hint' | 'error'>>(
  ({ autoGrow, maxHeight, className, rows = 4, value, ...rest }, ref) => {
    const field = useField();
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    // Layout effect: the height is set before paint, so the field does not jump on each keystroke.
    useLayoutEffect(() => {
      const element = innerRef.current;
      if (!autoGrow || !element) return;
      element.style.height = '';
      // +2: scrollHeight leaves out the border.
      const height = element.scrollHeight + 2;
      element.style.height = `${maxHeight ? Math.min(height, maxHeight) : height}px`;
    }, [autoGrow, maxHeight, value]);

    return (
      <textarea
        ref={innerRef}
        id={field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        rows={rows}
        value={value}
        className={cx(
          controlClasses(Boolean(field?.invalid)),
          'px-4 py-3',
          !autoGrow ? 'resize-y' : maxHeight ? 'resize-none overflow-y-auto' : 'resize-none overflow-hidden',
          className,
        )}
        {...rest}
      />
    );
  },
);
Control.displayName = 'TextareaControl';

/** The one multi-line field. With `label`, `hint` or `error` it brings its own `Field`. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, required, ...rest }, ref) => {
    if (!label && !hint && !error) return <Control ref={ref} required={required} {...rest} />;

    return (
      <Field label={label} hint={hint} error={error} required={required}>
        <Control ref={ref} required={required} {...rest} />
      </Field>
    );
  },
);
Textarea.displayName = 'Textarea';
